import { inject, Injector, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { PlanLimitReason, PlanLimitRejection, planLimitMessage } from './plan-limit';
import { PLAN_LIMIT_TOAST_DEBOUNCE_MS } from './tenant-lifecycle.config';

/**
 * Tells the user why a create was blocked by the plan of the workspace
 * (403 with reason `plan_limit_podcasts` or `plan_limit_seats`), as a toast
 * that points to a plan upgrade. No new screens; the calling page keeps its
 * own error handling.
 */
@Service()
export class PlanLimitService {
  // Resolved lazily, like TenantLifecycleService: also usable in tests without toast/i18n providers.
  private readonly injector = inject(Injector);
  private readonly lastToastAt = new Map<PlanLimitReason, number>();

  /** Shows the toast, debounced per reason, because a page may send the same create twice. */
  handleRejection(rejection: PlanLimitRejection, now: number = Date.now()): void {
    const last = this.lastToastAt.get(rejection.reason) ?? Number.NEGATIVE_INFINITY;
    if (now - last < PLAN_LIMIT_TOAST_DEBOUNCE_MS) {
      return;
    }
    this.lastToastAt.set(rejection.reason, now);

    const message = planLimitMessage(rejection);
    const translate = this.injector.get(TranslateService, null);
    const text: unknown = translate?.instant(message.key, message.params) ?? message.key;
    this.injector.get(ToastrService, null)?.warning(typeof text === 'string' ? text : message.key);
  }
}
